/**
 * This file is for exporting everything that we want people to use from within Bento Web.
 * Anything not reachable from this file's exports should be considered internal-only and
 * subject to change at any time.
 *
 * TypeScript types need to exported as type-only exports (i.e. `export type X`) to ensure that
 * the TS compiler elides them properly from the JS build output. If we export them as `export X`,
 * the compiler can't tell that they're just types and not values, so it includes them in the
 * JS export. For more background, see
 * https://devblogs.microsoft.com/typescript/announcing-typescript-3-8/#type-only-imports-exports.
 * We collect these at the bottom of the file for clarity.
 */

// Workaround to support re-exporting types with Babel + tsc
// https://github.com/babel/babel-loader/issues/603
// https://stackoverflow.com/questions/52258061/export-not-found-on-module
import type {
  MenuOptionsGroupType,
  MenuOptionsListType,
  MenuOptionVariantSelectable,
} from './product/components/MenuOption'
import type * as ProductThemes from './product/themes'

export { Accordion, AccordionPhase } from './product/components/Accordion'
export { AchromaticThemeOverride } from './product/components/AchromaticThemeOverride'
export { AlertSheet } from './product/components/AlertSheet'
export { AnchorTable } from './product/components/AnchorTable'
export { Autocomplete } from './product/components/Autocomplete'
export { DateInput, isDate } from './product/components/DateInput'
export { DataRow } from './product/components/DataRow'
export { NavigationRow } from './product/components/NavigationRow'
export { ValuePropRow } from './product/components/ValuePropRow'
export {
  SelectionRow,
  SelectionRowRadioGroupProvider,
} from './product/components/SelectionRow'
export { RadioButton, RadioButtonGroup } from './product/components/RadioButton'
export { SSNInput } from './product/components/SSNInput'
export {
  Spinner12,
  Spinner16,
  Spinner24,
  SpinnerHero,
  SpinnerOverlay,
} from './product/components/Spinner/index'
export {
  BentoProvider,
  ThemelessBentoProvider,
} from './product/components/BentoProvider'
export { Button } from './product/components/Button'
export { IconButton } from './product/components/IconButton'
export { TextButton } from './product/components/TextButton'
export { BaseButton } from './product/components/BaseButton'
export { Card } from './product/components/Card'
export { Checkbox } from './product/components/Checkbox'
export { CheckboxList } from './product/components/CheckboxList'
export { Chip } from './product/components/Chip'
export { CurrencyInput } from './product/components/CurrencyInput'
export { ExpandableRow } from './product/components/ExpandableRow'
export { SettingsRow } from './product/components/SettingsRow'
export { ExperimentalTicker } from './product/components/Ticker'
export { FocusOn } from './components/FocusOn'
export {
  HoverState,
  LowOpacityHoverState,
  BackgroundColorHoverState,
  ForegroundColorHoverState,
  ForegroundColorPrimaryHoverState,
} from './product/components/HoverState'
export { LinkProvider } from './components/LinkProvider'
export { Markdown } from './product/components/Markdown'
export { Modal } from './product/components/Modal'
export { ModalBackdrop } from './product/components/ModalBackdrop'
export { OverflowMenu } from './product/components/OverflowMenu'
export { Popover } from './product/components/Popover'
export { AnchoredOverlay } from './product/components/AnchoredOverlay'
export { PrimaryColorOverride } from './product/components/PrimaryColorOverride'
export { RadioButtonList } from './product/components/RadioButtonList'
export { RichText } from './product/components/RichText'
export { SemanticColorOverride } from './product/components/SemanticColorOverride'
export { ScaryOverride } from './product/components/ScaryOverride'
export { Tabs } from './product/components/Tabs'
export {
  TextColorOverride,
  _InternalTextColorOverride as ExperimentalTextColorOverride,
} from './product/components/TextColorOverride'
export { TextArea } from './product/components/TextArea'
export { TextInput, getInputState } from './product/components/TextInput'
export { Timeline } from './product/components/Timeline'
export { TooltipTrigger } from './product/components/TooltipTrigger'
export { PhoneNumberInput } from './product/components/PhoneNumberInput'
export { PasswordInput } from './product/components/PasswordInput'
export { NumberInput } from './product/components/NumberInput'
export { Layer, LayersProvider } from './product/components/Layer'
export { Logo } from './product/components/Logo'
export {
  ThemeProvider,
  getPrimaryColorVars,
  getNeutralColorVars,
  getSemanticColorVars,
  getShadowColorVars,
  getTextColorVar,
  useTheme,
  useThemeColorComponents,
  useThemeColorBase,
  useThemeColorLight,
  useThemeColorLightest,
  useSemanticColorBase,
} from './product/components/ThemeProvider'
export { Toggle } from './product/components/Toggle'
export { Type } from './product/components/Type'
export { CollapsibleText } from './product/components/CollapsibleText'
export { SelectMenu } from './product/components/SelectMenu'
export { ExperimentalChipMenu } from './product/components/ChipMenu'
export { Skeleton } from './product/components/Skeleton'
export { InfoBanner } from './product/components/InfoBanner'
export { CelebrationCard } from './product/components/CelebrationCard'
export { StatusBanner } from './product/components/StatusBanner'
export { Takeover, CloseTakeoverButton } from './product/components/Takeover'
export { InfoTag } from './product/components/InfoTag'
export {
  FoundationIcon,
  getIsSize12Icon,
  getIsSize16Icon,
  getIsSize24Icon,
  getIsSize32Icon,
  getIsSize40Icon,
  isValidFoundationIconType,
} from './product/components/FoundationIcon'
export { Pog } from './product/components/Pog'
export { NumberPog } from './product/components/NumberPog'
export { useToast, ToastProvider } from './product/components/Toast'
export { SegmentedControl } from './product/components/SegmentedControl'
export {
  LayoutRoot as ExperimentalLayoutRoot,
  Container as ExperimentalContainer,
  LayoutColumnWithRegrets as ExperimentalLayoutColumnWithRegrets,
  LayoutRowWithRegrets as ExperimentalLayoutRowWithRegrets,
  mediaQueryForLayoutSizeLGOrLarger as mediaQueryForExperimentalLayoutSizeLGOrLarger,
  mediaQueryForLayoutSizeMDOrLarger as mediaQueryForExperimentalLayoutSizeMDOrLarger,
  mediaQueryForLayoutSizeMDOrSmaller as mediaQueryForExperimentalLayoutSizeMDOrSmaller,
  mediaQueryForLayoutSizeXLOrLarger as mediaQueryForExperimentalLayoutSizeXLOrLarger,
} from './product/components/Layout'
export { ThemelessLegacySpinner } from './components/ThemelessLegacySpinner'
export { ThemelessRobinhoodLogotype } from './components/ThemelessRobinhoodLogotype'

export {
  NeutralColorToken,
  SemanticColorToken,
  OverlayColorToken,
  CssVars,
  lightTheme,
  darkTheme,
  cryptoTheme,
  scaryTheme,
  creditGoldTheme,
  lightUpTheme,
  lightDownTheme,
  lightUpAccessibleColorsTheme,
  lightDownAccessibleColorsTheme,
  lightGoldTheme,
  lightGoldAccessibleColorsTheme,
  darkUpTheme,
  darkDownTheme,
  darkUpAccessibleColorsTheme,
  darkDownAccessibleColorsTheme,
  darkGoldTheme,
  darkGoldAccessibleColorsTheme,
  darkCryptoUpTheme,
  darkCryptoDownTheme,
  darkCryptoUpAccessibleColorsTheme,
  darkCryptoDownAccessibleColorsTheme,
  lightCryptoUpTheme,
  lightCryptoDownTheme,
  lightCryptoUpAccessibleColorsTheme,
  lightCryptoDownAccessibleColorsTheme,
  scaryAccessibleColorsTheme,
} from './product/themes'

export {
  NeutralPaletteColorName,
  ExtendedPaletteColorName,
  getNeutralColor,
  getNeutralColorComponents,
  getLightThemeExtendedColorComponents,
  getDarkThemeExtendedColorComponents,
  getLightThemeExtendedColor,
  getDarkThemeExtendedColor,
} from './shared/MobiusColors'

export { UnstyledButton } from './components/UnstyledButton'
export { UnstyledAnchor } from './components/UnstyledAnchor'

/**
 * TypeScript types should be exported down here.
 */
export type ProductTheme = ProductThemes.ProductTheme
export type { ColorToken, ThemeColorToken } from './product/themes'
export type {
  Icon,
  Icon12,
  Icon16,
  Icon24,
  Icon32,
  Icon40,
} from './product/components/FoundationIcon/icons'
export type {
  ReactAnchorProps,
  ReactButtonProps,
  ReactInputProps,
} from './types'
export type MenuOption<T extends string = string> =
  MenuOptionVariantSelectable<T>
export type MenuOptionsList<T extends MenuOption = MenuOption> =
  MenuOptionsListType<T>
export type MenuOptionsGroup<T extends MenuOption = MenuOption> =
  MenuOptionsGroupType<T>
